// /scada/reports — reports catalogue + sample preview (R4). Mock numbers.
//
// Стиль приведено до кабінету: картки — та сама скляна поверхня з тінню, що
// й .tile; контроли — нейтральна мова SegmentedTabs (32/8, обводка
// --line-strong). Брендовий червоний прибрано з кнопок «Сформувати»,
// кнопок формату й контуру вибраної картки: §3 лишає --err за реальною
// помилкою, а не за активним станом контрола.
import { useMemo, useState } from 'react';
import { Download, FileBarChart } from 'lucide-react';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { STATIONS } from '@/scada/mock/stations';
import { useAllowedStationIds } from '@/scada/useScopedStations';
import { useScadaStation } from '@/scada/useScadaStation';
import { snapshotFleet } from '@/scada/mock/telemetry';

interface ReportDef { id: string; title: string; desc: string; period: string; formats: string[]; preview: boolean; }
const REPORTS: ReportDef[] = [
  { id: 'daily', title: 'Оперативний добовий', desc: 'Генерація, аварії, обмеження, команди, звʼязок, невирішені питання', period: 'Доба', formats: ['Web', 'PDF', 'Excel'], preview: true },
  { id: 'monthly', title: 'Місячний сервісний', desc: 'План, факт, виконання, події, втрати, статистика обладнання, висновок', period: 'Місяць', formats: ['Word', 'PDF', 'Excel'], preview: true },
  { id: 'losses', title: 'Втрати та небаланси', desc: 'Небаланси, втрати від обладнання, СКО, операційної безпеки, обмежень, УЗЕ', period: 'Будь-який', formats: ['Excel', 'CSV'], preview: false },
  { id: 'sko', title: 'СКО та обмеження', desc: 'Команди, підтвердження, виконання, відбій, недовиробіток, компенсація', period: 'Доба / місяць', formats: ['PDF', 'Excel'], preview: false },
  { id: 'avail', title: 'Доступність обладнання', desc: 'Коефіцієнт доступності, простої, статистика виходів обладнання', period: 'Місяць', formats: ['PDF', 'Excel'], preview: false },
  { id: 'exec', title: 'Звіт для керівника', desc: 'Стисла картина: генерація, втрати, топ проблем, виконання прогнозу, ефект', period: 'Тиждень / місяць', formats: ['PDF'], preview: false },
];

// Нейтральний контрол: та сама висота, радіус і обводка, що й у сегментованих
// перемикачах кабінету. Один опис на всі кнопки сторінки.
const CTRL = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  height: 32, borderRadius: 8, padding: '0 12px',
  background: 'transparent',
  border: '1px solid var(--line-strong)',
  color: 'var(--text-muted)',
  fontSize: 12, fontWeight: 600, cursor: 'pointer',
  transition: 'background 160ms ease, color 160ms ease',
} as const;
const CTRL_ON = {
  ...CTRL,
  background: 'color-mix(in srgb, var(--text) 6%, transparent)',
  color: 'var(--text)',
} as const;
const hoverOn = (e: React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.background = 'color-mix(in srgb, var(--text) 10%, transparent)';
};
const hoverOff = (on: boolean) => (e: React.MouseEvent<HTMLButtonElement>) => {
  e.currentTarget.style.background = on ? 'color-mix(in srgb, var(--text) 6%, transparent)' : 'transparent';
};

export default function ScadaReportsPage() {
  const [sel, setSel] = useState<string | null>(null);
  const fleet = useMemo(() => snapshotFleet(), []);
  const allowed = useAllowedStationIds();
  const station = useScadaStation();

  const rows = STATIONS
    .filter((s) => (!allowed || allowed.has(s.id)) && station.matchesId(s.id))
    .map((s) => {
    const t = fleet.find((f) => f.stationId === s.id);
    const fact = t ? t.dailyEnergyKwh / 1000 : 0;
    const plan = +(s.acCapacityKw * 4.9 / 1000).toFixed(1);
    const done = plan ? Math.round((fact / plan) * 100) : 0;
    return { name: s.name, plan, fact: +fact.toFixed(1), done, limited: !!t?.limitReason, offline: t?.state === 'offline' };
  });
  const totPlan = rows.reduce((a, r) => a + r.plan, 0);
  const totFact = rows.reduce((a, r) => a + r.fact, 0);
  const totDone = totPlan ? Math.round((totFact / totPlan) * 100) : 0;

  const selDef = REPORTS.find((r) => r.id === sel);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" style={{ marginBottom: sel ? 16 : 0 }}>
        {REPORTS.map((r) => (
          <div key={r.id} className="flex flex-col" style={{
            background: scada.panel,
            // Вибрана картка позначається виразнішим контуром, а не кольором.
            border: `1px solid ${sel === r.id ? 'var(--line-strong)' : scada.border}`,
            borderRadius: 12, padding: 14,
            boxShadow: 'var(--shadow)',
          }}>
            <div style={{ color: scada.text, fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{r.title}</div>
            <div style={{ color: scada.textMuted, fontSize: 12, marginBottom: 8, minHeight: 48 }}>{r.desc}</div>
            <div className="flex items-center gap-1 flex-wrap" style={{ marginBottom: 10 }}>
              <span style={{ color: scada.textDim, fontSize: 11, marginRight: 4 }}>{r.period}</span>
              {r.formats.map((f) => (
                <span key={f} style={{ background: scada.panel2, color: scada.textDim, border: `1px solid ${scada.border}`, borderRadius: 4, padding: '1px 6px', fontSize: 10 }}>{f}</span>
              ))}
            </div>
            <button
              onClick={() => setSel(r.id)}
              style={{ ...(sel === r.id ? CTRL_ON : CTRL), width: '100%', marginTop: 'auto' }}
              onMouseEnter={hoverOn}
              onMouseLeave={hoverOff(sel === r.id)}
            >
              {sel === r.id ? 'Сформовано' : 'Сформувати'}
            </button>
          </div>
        ))}
      </div>

      {selDef && selDef.preview && (
        <SectionCard
          title={`${selDef.title} — попередній перегляд`}
          subtitle={`${new Date().toLocaleDateString('uk-UA')} · демонстраційні дані`}
          right={(
            <div className="flex gap-1.5">
              {selDef.formats.map((f) => (
                <button key={f} style={{ ...CTRL, height: 28, padding: '0 10px', fontSize: 11 }}
                  onMouseEnter={hoverOn} onMouseLeave={hoverOff(false)}>
                  <Download size={12} strokeWidth={2} /> {f}
                </button>
              ))}
            </div>
          )}
          padded={false}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: scada.panel2 }}>
                {['Станція', 'План, МВт·год', 'Факт, МВт·год', 'Виконання', 'Примітки'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '8px 12px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} style={{ borderTop: `1px solid ${scada.border}` }}>
                  <td style={{ padding: '8px 12px', color: scada.text, fontSize: 13 }}>{r.name}</td>
                  <td style={{ padding: '8px 12px', color: scada.textDim, fontSize: 13 }}>{r.plan}</td>
                  <td style={{ padding: '8px 12px', color: scada.text, fontSize: 13 }}>{r.fact}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {/* Недовиконання плану — це увага (--warn), а не аварія:
                        червоний лишається за реальною помилкою (§3). */}
                    <span style={{ color: r.done >= 90 ? scada.state.ok : scada.state.warning, fontSize: 13, fontWeight: 600 }}>{r.done}%</span>
                  </td>
                  <td style={{ padding: '8px 12px', color: scada.textMuted, fontSize: 12 }}>
                    {r.offline ? 'втрата звʼязку' : r.limited ? 'діяло обмеження' : '—'}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: `2px solid ${scada.borderStrong}`, background: scada.panel2 }}>
                <td style={{ padding: '8px 12px', color: scada.text, fontSize: 13, fontWeight: 700 }}>Разом</td>
                <td style={{ padding: '8px 12px', color: scada.textDim, fontSize: 13, fontWeight: 700 }}>{totPlan.toFixed(1)}</td>
                <td style={{ padding: '8px 12px', color: scada.text, fontSize: 13, fontWeight: 700 }}>{totFact.toFixed(1)}</td>
                <td style={{ padding: '8px 12px', color: totDone >= 90 ? scada.state.ok : scada.state.warning, fontSize: 13, fontWeight: 700 }}>{totDone}%</td>
                <td style={{ padding: '8px 12px' }}></td>
              </tr>
            </tbody>
          </table>
        </SectionCard>
      )}

      {selDef && !selDef.preview && (
        <SectionCard title={`${selDef.title}`} subtitle="Звіт сформовано (демонстрація)">
          <div className="flex items-center gap-3">
            <FileBarChart size={26} strokeWidth={1.8} style={{ color: scada.textDim, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: scada.text, fontSize: 13 }}>{selDef.desc}</div>
              <div style={{ color: scada.textMuted, fontSize: 12 }}>Період: {selDef.period}</div>
            </div>
            <div className="flex gap-1">
              {selDef.formats.map((f) => (
                <button key={f} style={CTRL} onMouseEnter={hoverOn} onMouseLeave={hoverOff(false)}>
                  <Download size={13} strokeWidth={2} /> {f}
                </button>
              ))}
            </div>
          </div>
        </SectionCard>
      )}
    </>
  );
}
